import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SortDirection } from '@swimlane/ngx-datatable';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { X_SKIP_ALERT } from 'src/app/core/constants/http-headers';
import { JQueryTableData } from 'src/app/core/models/jquery-table-data';
import { MemberAccessSummary } from 'src/app/core/models/member-access-summary';
import { MessageForm, MessageStatus, SendMessage } from 'src/app/core/models/message-form';
import { UserForTag } from 'src/app/core/models/user';
import { CombinedCountData } from '../../private/messenger/unread-message-count/unread-message-count.component';

const messageCountUrl = '/patients/fetchusermessagesCount.html';
const sentMessagesUrl = '/patients/fetchuserSentMessages.html';
const receivedMessagesUrl = '/patients/fetchuserReceiveMessages.html';
const respondToMessageUrl = '/patients/respondtomessge.html';
const sendMessageUrl = '/patients/sendmessage.html';
const searchRecipientsUrl = '/users/searchUsersForTags.html';
const updateReceiverMessageStatusUrl = '/patients/updatereceiverresponse.html';
const markAllAsReadUrl = '/patients/updateReceiverResponseForAll.html';
const getThreadedMessagesUrl = '/patients/fetchThreadedMessages.html';
const getPatientMessagesUrl = '/patients/fetchPatientMessages.html';
const getRecipientMemberAccessSummaryUrl = '/patients/getRecipientMemberAccessSummary.html';
const getCombinedCountUrl = '/counts/fetchCounts';

@Injectable({
  providedIn: 'root'
})
export class MessageService {
  private _messageSent: Subject<void> = new Subject();
  messageSent: Observable<void> = this._messageSent.asObservable();

  private _messageCountUpdated: BehaviorSubject<number> = new BehaviorSubject(null);
  messageCountUpdated: Observable<number> = this._messageCountUpdated.asObservable();

  get messageCount(): number {
    return this._messageCountUpdated.value;
  }

  constructor(private http: HttpClient) {}

  public getReceivedMessages(
    senderName: string,
    message: string,
    tag: string,
    iDisplayStart: number,
    iDisplayLength: number,
    sortDirection: SortDirection,
    sortColumn: string,
    subject: string,
    otherSubject: string,
    patientName: string
  ): Observable<JQueryTableData<MessageForm>> {
    if (subject === 'null') {
      subject = '';
    }

    return this.http.get<JQueryTableData<MessageForm>>(receivedMessagesUrl, {
      params: {
        status: `${MessageStatus.READ};${MessageStatus.UNREAD}`,
        senderName: senderName || '',
        message: message || '',
        tag: tag || '',
        iDisplayStart: iDisplayStart,
        iDisplayLength: iDisplayLength,
        iSortCol_0: sortColumn,
        sSortDir_0: sortDirection,
        subject: subject || '',
        otherSubject: otherSubject || '',
        patientName: patientName || ''
      }
    });
  }

  public getSentMessages(
    receiverName: string,
    message: string,
    tag: string,
    iDisplayStart: number,
    iDisplayLength: number,
    sortDirection: SortDirection,
    sortColumn: string,
    subject: string,
    otherSubject: string,
    patientName: string
  ): Observable<JQueryTableData<MessageForm>> {
    if (subject === 'null') {
      subject = '';
    }

    return this.http.get<JQueryTableData<MessageForm>>(sentMessagesUrl, {
      params: {
        receiverName: receiverName || '',
        message: message || '',
        tag: tag || '',
        iDisplayStart: iDisplayStart,
        iDisplayLength: iDisplayLength,
        iSortCol_0: sortColumn,
        sSortDir_0: sortDirection,
        subject: subject || '',
        otherSubject: otherSubject || '',
        patientName: patientName || ''
      }
    });
  }

  public markAllAsRead(receiverid: number): Observable<boolean> {
    return this.http.post<boolean>(markAllAsReadUrl, '', {
      params: {
        receiverid: receiverid,
        targetStatus: MessageStatus.READ,
        currentStatus: MessageStatus.UNREAD
      }
    });
  }

  public markMessageAsRead(messageId: number, parentMessageId: number, receiverId: number): Observable<boolean> {
    return this.updateMessageStatus(messageId, parentMessageId, receiverId, MessageStatus.READ);
  }

  public searchRecipients(term: string, memberId?: string): Observable<UserForTag[]> {
    let params = new HttpParams().set('term', term);

    if (memberId) {
      params = params.set('memberId', memberId);
    }

    return this.http.get<UserForTag[]>(searchRecipientsUrl, {
      params: params
    });
  }

  respondToMessage(messageForm: MessageForm, oldMessageId: number, isReplyAll: boolean): Observable<MessageForm> {
    return this.http.post<MessageForm>(respondToMessageUrl, messageForm, {
      params: {
        oldMessageId: oldMessageId,
        replyAll: isReplyAll
      }
    });
  }

  getThreadedMessages(parentId: number): Observable<MessageForm[]> {
    return this.http.get<MessageForm[]>(getThreadedMessagesUrl, {
      params: {
        parentId: parentId
      }
    });
  }

  getPatientMessages(
    memberId: string,
    iDisplayStart: number,
    iDisplayLength: number
  ): Observable<JQueryTableData<MessageForm>> {
    return this.http.get<JQueryTableData<MessageForm>>(getPatientMessagesUrl, {
      params: {
        memberId: memberId,
        iDisplayStart: iDisplayStart,
        iDisplayLength: iDisplayLength
      }
    });
  }

  getRecipientMemberAccessSummary(messageId: number) {
    return this.http.get<MemberAccessSummary>(getRecipientMemberAccessSummaryUrl, {
      params: {
        messageId: messageId
      }
    });
  }

  getUnreadMessageCount(): Observable<number> {
    return this.getMessageCount(MessageStatus.UNREAD);
  }

  sendMessage(sendMessage: SendMessage): Observable<MessageForm> {
    return this.http
      .post<MessageForm>(sendMessageUrl, sendMessage, {
        params: {
          patientId: sendMessage.patientId || '',
          recipient: sendMessage.recipientUserIds.join(','),
          subject: sendMessage.subject || '',
          otherSubject: sendMessage.otherSubject || ''
        }
      })
      .pipe(
        tap((message) => {
          if (message) {
            this._messageSent.next();
          }
        })
      );
  }

  private createParams(lastNotificationDate?: Date): HttpParams {
    let httpParams = new HttpParams().set('status', MessageStatus.UNREAD);

    if (lastNotificationDate) {
      httpParams = httpParams.set('lastNotificationDate', lastNotificationDate.toISOString());
    }

    return httpParams;
  }

  public getCombinedCount(lastNotificationDate?: Date): Observable<CombinedCountData> {
    return this.http.get(getCombinedCountUrl, {
      headers: {
        [X_SKIP_ALERT]: '1'
      },
      params: this.createParams(lastNotificationDate)
    });
  }

  private getMessageCount(status: string): Observable<number> {
    return this.http
      .get(messageCountUrl, {
        responseType: 'text',
        headers: {
          [X_SKIP_ALERT]: '1'
        },
        params: {
          status: status
        }
      })
      .pipe(
        map((messageCount) => {
          const count = parseInt(messageCount) || 0;

          this._messageCountUpdated.next(count);

          return count;
        })
      );
  }

  private updateMessageStatus(
    messageId: number,
    parentMessageId: number,
    receiverId: number,
    status: string
  ): Observable<boolean> {
    return this.http.post<boolean>(updateReceiverMessageStatusUrl, '', {
      params: {
        messageId: messageId || 0,
        parentMessageId: parentMessageId || 0,
        receiverId: receiverId || 0,
        status: status
      }
    });
  }

  resetMessageCount() {
    this._messageCountUpdated.next(0);
  }
}
